/**
 * "Go to a world" in the headset, operated by controller ray (or hand pinch):
 * tabs + search (3D keyboard) + a paged list with thumbnails → the world's
 * page with Go, the people here to invite, and "Invite everyone here".
 * Plus the invitee's prompt: "<Name> invited you to <World> — Go / Not now".
 * Both replace the VR user menu while shown (like the share panel).
 */
import React, { Suspense, useEffect, useMemo, useState } from 'react';
import { Text, useTexture } from '@react-three/drei';
import { SRGBColorSpace } from 'three';
import { vrColors as colors, noRaycast, Surface } from '../VR/vrUi';
import VRKeyboard, { KEY, type KeyAction } from '../VR/VRKeyboard';
import { FallbackOnError } from '../three/FallbackOnError';
import { HOP_SECTIONS, acceptInvite, declineInvite, useWorldHop, type HopSection, type IncomingInvite } from '../../services/worldHop';
import { useWorldHopPicker } from './useWorldHopPicker';

const W = .8;
const cut = (s: string, n: number) => (s.length > n ? `${s.slice(0, n - 1)}…` : s);

function Button({ label, x, y, w = .17, h = .056, primary = false, active = false, disabled = false, name, onPress }: {
  label: string; x: number; y: number; w?: number; h?: number; primary?: boolean; active?: boolean; disabled?: boolean; name?: string; onPress: () => void;
}) {
  const [hover, setHover] = useState(false);
  const bg = disabled ? '#1a2633' : primary ? (hover ? '#2ee6d6' : colors.teal) : active ? (hover ? '#1a6157' : '#124940') : hover ? colors.hover : colors.card;
  return <group position={[x, y, .006]} name={name}
    onPointerOver={(e) => { e.stopPropagation(); setHover(true); }} onPointerOut={() => setHover(false)}
    onClick={(e) => { e.stopPropagation(); if (!disabled) onPress(); }}>
    <Surface width={w} height={h} color={bg} r={.012} />
    <Text raycast={noRaycast} position={[0, 0, .003]} fontSize={.018} maxWidth={w - .02}
      color={disabled ? colors.muted : primary ? '#04211e' : active ? '#71e2cb' : colors.text}>{label}</Text>
  </group>;
}

function ThumbTex({ url, w, h }: { url: string; w: number; h: number }) {
  const tex = useTexture(url);
  tex.colorSpace = SRGBColorSpace;
  return <mesh raycast={noRaycast}><planeGeometry args={[w, h]} /><meshBasicMaterial map={tex} toneMapped={false} /></mesh>;
}
/** World thumbnail quad; the GathrXR hero if it has none or it won't load. */
function Thumb({ url, w, h }: { url: string | null; w: number; h: number }) {
  const plain = <mesh raycast={noRaycast}><planeGeometry args={[w, h]} /><meshBasicMaterial color="#163246" toneMapped={false} /></mesh>;
  return <FallbackOnError key={url || 'none'} what="world thumbnail" fallback={plain}>
    <Suspense fallback={plain}><ThumbTex url={url || '/gathrxr-hero.webp'} w={w} h={h} /></Suspense>
  </FallbackOnError>;
}

function WorldRow({ y, name, meta, thumb, onPress, id }: { y: number; name: string; meta: string; thumb: string | null; onPress: () => void; id: string }) {
  const [hover, setHover] = useState(false);
  return <group position={[0, y, .006]} name={`vr-hop-world-${id}`}
    onPointerOver={(e) => { e.stopPropagation(); setHover(true); }} onPointerOut={() => setHover(false)}
    onClick={(e) => { e.stopPropagation(); onPress(); }}>
    <Surface width={W - .06} height={.078} color={hover ? colors.hover : colors.card} r={.012} />
    <group position={[-W / 2 + .1, 0, .003]}><Thumb url={thumb} w={.1} h={.0625} /></group>
    <Text raycast={noRaycast} position={[-W / 2 + .17, .013, .003]} anchorX="left" fontSize={.02} color={colors.text}>{cut(name, 34)}</Text>
    <Text raycast={noRaycast} position={[-W / 2 + .17, -.017, .003]} anchorX="left" fontSize={.013} color={colors.muted}>{meta}</Text>
    <Text raycast={noRaycast} position={[W / 2 - .06, 0, .003]} fontSize={.026} color={hover ? colors.teal : colors.muted}>›</Text>
  </group>;
}

const PAGE = 5;

export function VRWorldHopPanel({ worldId, onClose }: { worldId?: string; onClose: () => void }) {
  const p = useWorldHopPicker(worldId, true);
  const [tab, setTab] = useState<HopSection | 'all'>('all');
  const [page, setPage] = useState(0);
  const [typing, setTyping] = useState(false);
  const list = useMemo(() => p.shown.filter((w) => tab === 'all' || w.section === tab), [p.shown, tab]);
  useEffect(() => setPage(0), [tab, p.query]);
  const pages = Math.max(1, Math.ceil(list.length / PAGE));
  const rows = list.slice(page * PAGE, page * PAGE + PAGE);
  const onKey = (a: KeyAction) => {
    if (a.type === 'backspace') p.setQuery(p.query.slice(0, -1));
    else if (a.type === 'go') setTyping(false);
    else p.setQuery((p.query + a.value).slice(0, 40));
  };
  const sel = p.selected;
  const top = .34, bottom = -.36, H = top - bottom;

  return <group onClick={(e) => e.stopPropagation()} name="vr-worldhop-panel">
    <group position={[0, (top + bottom) / 2, 0]}>
      <Surface width={W + .008} height={H + .008} color={colors.border} z={-.003} />
      <Surface width={W} height={H} color={colors.panel} />
    </group>
    {sel && <Button label="‹ Back" x={-.32} y={.305} w={.12} name="vr-hop-back" onPress={() => p.choose(null)} />}
    <Text raycast={noRaycast} position={[sel ? -.245 : -.37, .305, .006]} anchorX="left" fontSize={.024} color={colors.text}>
      {sel ? cut(sel.name, 26) : 'Go to a world'}
    </Text>
    <Button label="Close" x={.32} y={.305} w={.12} name="vr-hop-close" onPress={onClose} />

    {!sel ? <>
      {/* Tabs */}
      {([['all', 'All'], ...HOP_SECTIONS.map((s) => [s.key, s.short])] as [HopSection | 'all', string][]).map(([k, label], i) => (
        <Button key={k} label={label} x={-.3 + i * .15} y={.24} w={.14} h={.05} active={tab === k} name={`vr-hop-tab-${k}`} onPress={() => setTab(k)} />
      ))}
      {/* Search */}
      <group position={[-.1, .175, .006]}>
        <Surface width={.54} height={.056} color="#0a141f" r={.012} />
        {typing && <group position={[0, 0, -.001]}><Surface width={.546} height={.062} color={colors.teal} r={.014} /></group>}
        <Text raycast={noRaycast} position={[-.255, 0, .003]} anchorX="left" fontSize={.019} color={p.query ? colors.text : colors.muted}>
          {p.query ? `${cut(p.query, 40)}${typing ? '▏' : ''}` : 'Search worlds'}
        </Text>
      </group>
      <Button label={typing ? 'Done' : 'Search'} x={.24} y={.175} w={.12} active={typing} name="vr-hop-search" onPress={() => setTyping((v) => !v)} />
      <Button label="Clear" x={.335} y={.175} w={.06} disabled={!p.query} onPress={() => p.setQuery('')} />

      {p.worlds === null ? <Text raycast={noRaycast} position={[0, 0, .006]} fontSize={.02} color={colors.muted}>Loading worlds…</Text>
        : list.length === 0 ? <Text raycast={noRaycast} position={[0, 0, .006]} fontSize={.02} color={colors.muted} maxWidth={.7}>
          {p.error || (p.query ? 'No worlds match that search.' : 'No worlds here yet.')}
        </Text>
        : rows.map((w, i) => (
          <WorldRow key={w.id} id={w.id} y={.1 - i * .088} name={w.name} thumb={w.thumb} onPress={() => p.choose(w)}
            meta={`${HOP_SECTIONS.find((s) => s.key === w.section)?.label} · ${w.isPublic ? 'Public' : 'Private'}${w.ownerName && w.section !== 'mine' ? ` · ${cut(w.ownerName, 18)}` : ''}`} />
        ))}
      {pages > 1 && <>
        <Button label="‹ Prev" x={-.25} y={-.325} w={.14} h={.046} disabled={page === 0} onPress={() => setPage((n) => Math.max(0, n - 1))} />
        <Text raycast={noRaycast} position={[0, -.325, .006]} fontSize={.016} color={colors.muted}>{`${page + 1} / ${pages}`}</Text>
        <Button label="Next ›" x={.25} y={-.325} w={.14} h={.046} disabled={page >= pages - 1} name="vr-hop-next" onPress={() => setPage((n) => Math.min(pages - 1, n + 1))} />
      </>}
      {typing && <group position={[0, bottom - .02 - KEY / 2, .006]}><VRKeyboard onKey={onKey} mode="text" goLabel="Done" /></group>}
    </> : <>
      <group position={[-.24, .155, .006]}><Thumb url={sel.thumb} w={.26} h={.1625} /></group>
      <Text raycast={noRaycast} position={[-.08, .215, .006]} anchorX="left" fontSize={.016} color={colors.teal}>
        {sel.isPublic ? 'PUBLIC' : 'PRIVATE'}
      </Text>
      {sel.ownerName && <Text raycast={noRaycast} position={[-.08, .18, .006]} anchorX="left" fontSize={.016} color={colors.muted}>{`by ${cut(sel.ownerName, 24)}`}</Text>}
      <Button label="Go now" x={.06} y={.105} w={.26} h={.062} primary name="vr-hop-go" onPress={p.go} />

      <Text raycast={noRaycast} position={[-.37, .025, .006]} anchorX="left" fontSize={.019} color={colors.text}>Bring people along</Text>
      {p.inviteBlock
        ? <Text raycast={noRaycast} position={[-.37, -.03, .006]} anchorX="left" anchorY="top" fontSize={.017} color={colors.muted} maxWidth={.74}>{p.inviteBlock}</Text>
        : <>
          {p.people.slice(0, 6).map((person, i) => {
            const r = p.results[person.key];
            const on = p.picked.has(person.key);
            const label = `${r?.ok ? '✓ ' : on ? '● ' : '○ '}${cut(person.userName, 16)}${r?.ok ? ' · invited' : person.blockedReason ? ' · guest' : ''}`;
            return <Button key={person.key} label={label} x={i % 2 ? .19 : -.19} y={-.035 - Math.floor(i / 2) * .066} w={.36}
              active={on || !!r?.ok} disabled={!!person.blockedReason || !!r?.ok} name={`vr-hop-person-${i}`} onPress={() => p.toggle(person.key)} />;
          })}
          {p.people.length > 6 && <Text raycast={noRaycast} position={[0, -.225, .006]} fontSize={.014} color={colors.muted}>{`+${p.people.length - 6} more — use Invite everyone here`}</Text>}
          <Button label="Invite everyone here" x={-.19} y={-.27} w={.36} disabled={p.busy} name="vr-hop-invite-all" onPress={() => void p.invite(true)} />
          <Button label={p.busy ? 'Sending…' : `Invite${p.picked.size ? ` (${p.picked.size})` : ''}`} x={.19} y={-.27} w={.36}
            primary={p.picked.size > 0} disabled={p.busy || p.picked.size === 0} name="vr-hop-invite" onPress={() => void p.invite(false)} />
        </>}
      {p.status && <Text raycast={noRaycast} position={[0, -.33, .006]} fontSize={.016} color={colors.teal} maxWidth={.74}>{p.status}</Text>}
    </>}
  </group>;
}

/** The invitee's prompt in the headset. */
export function VRInvitePrompt({ invite }: { invite: IncomingInvite }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const go = async () => {
    setBusy(true);
    const e = await acceptInvite(invite);
    if (e) { setErr(e); setBusy(false); }
  };
  const w = .62, h = .24;
  return <group onClick={(e) => e.stopPropagation()} name="vr-invite-prompt">
    <Surface width={w + .008} height={h + .008} color={colors.teal} z={-.003} />
    <Surface width={w} height={h} color={colors.panel} />
    <Text raycast={noRaycast} position={[-w / 2 + .03, .085, .006]} anchorX="left" fontSize={.015} color={colors.teal}>WORLD INVITE</Text>
    <Text raycast={noRaycast} position={[0, .03, .006]} fontSize={.024} color={colors.text} maxWidth={w - .06} textAlign="center">
      {`${cut(invite.fromName, 24)} invited you to ${cut(invite.worldName, 30)}`}
    </Text>
    {err
      ? <Text raycast={noRaycast} position={[0, -.03, .006]} fontSize={.016} color="#ffb1ad" maxWidth={w - .06}>{err}</Text>
      : invite.grantsAccess && <Text raycast={noRaycast} position={[0, -.025, .006]} fontSize={.013} color={colors.muted}>It's private — going gives you visitor access.</Text>}
    <Button label={busy ? 'Going…' : 'Go'} x={-.13} y={-.075} w={.22} h={.06} primary disabled={busy} name="vr-invite-go" onPress={() => void go()} />
    <Button label="Not now" x={.13} y={-.075} w={.22} h={.06} disabled={busy} name="vr-invite-decline" onPress={() => declineInvite(invite)} />
  </group>;
}

/** Hook for the VR menu: which world-hop surface (if any) should replace it. */
export function useVRWorldHop() {
  const open = useWorldHop((s) => s.pickerOpen);
  const invite = useWorldHop((s) => s.invites[0]);
  const toast = useWorldHop((s) => s.toast);
  return { open, invite, toast, close: () => useWorldHop.getState().setPickerOpen(false), openPicker: () => useWorldHop.getState().setPickerOpen(true) };
}
